import React, { useState, useEffect, useRef } from 'react';
import { emailTemplatesAPI } from '../lib/api';
import api from '../lib/api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScheduledEmailsBanner } from './ScheduledEmailsBanner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Send,
  Calendar,
  Loader2,
  Paperclip,
  X,
  FileText,
  Image,
  File as FileIcon,
  Code,
  Type,
  Braces,
  ChevronDown,
  Eye,
} from 'lucide-react';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  STATUS_DISPLAY_NAMES,
} from '../lib/utils';

export const SendEmailDialog = ({ open, onOpenChange, customer, onEmailSent, presetTrigger = null }) => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('none');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [ccEmails, setCcEmails] = useState('hello@helioses.co.uk');
  const [fromEmail, setFromEmail] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [scheduleMode, setScheduleMode] = useState('now'); // 'now' | 'later'
  const [scheduleAt, setScheduleAt] = useState('');
  const [scheduledRefresh, setScheduledRefresh] = useState(0);
  const [editorMode, setEditorMode] = useState('text');
  const [showVarPicker, setShowVarPicker] = useState(false);
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [hostedImages, setHostedImages] = useState([]);
  const fileInputRef = useRef(null);
  const bodyRef = useRef(null);
  const varBtnRef = useRef(null);
  const imgBtnRef = useRef(null);
  const BASE_URL = '';

  useEffect(() => {
    if (open) {
      loadTemplates();
      loadHostedImages();
      setSelectedTemplate('none');
      setEmailSubject('');
      setEmailBody('');
      setCcEmails('hello@helioses.co.uk');
      setFromEmail('');
      setAttachments([]);
      setEditorMode('text');
      setShowVarPicker(false);
      setShowImagePicker(false);
    }
  }, [open]);

  useEffect(() => {
    if (!showVarPicker) return;
    const handleClick = (e) => {
      if (varBtnRef.current && !varBtnRef.current.contains(e.target)) setShowVarPicker(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showVarPicker]);

  useEffect(() => {
    if (!showImagePicker) return;
    const handleClick = (e) => {
      if (imgBtnRef.current && !imgBtnRef.current.contains(e.target)) setShowImagePicker(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showImagePicker]);

  const loadTemplates = async () => {
    try {
      const res = await emailTemplatesAPI.getAll();
      setTemplates(res.data || []);
    } catch {
      setTemplates([]);
    }
  };

  const loadHostedImages = async () => {
    try {
      const res = await api.get('/email-images');
      setHostedImages(res.data || []);
    } catch {
      setHostedImages([]);
    }
  };

  const insertImage = (img) => {
    const imageUrl = `${BASE_URL}/api/email-images/${img.slug}`;
    const tag = editorMode === 'html'
      ? `<img src="${imageUrl}" alt="${img.slug}" style="max-width:100%;" />`
      : imageUrl;

    const textarea = bodyRef.current;
    if (!textarea) {
      setEmailBody(prev => prev + tag);
      setShowImagePicker(false);
      return;
    }
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const before = emailBody.substring(0, start);
    const after = emailBody.substring(end);
    setEmailBody(before + tag + after);
    setShowImagePicker(false);
    requestAnimationFrame(() => {
      textarea.focus();
      const newPos = start + tag.length;
      textarea.setSelectionRange(newPos, newPos);
    });
  };

  const replaceVars = (text) => {
    if (!text || !customer) return text || '';
    const c = customer;
    const portalBase = 'https://app.helioses.co.uk';
    // Placeholder URL — backend generates a real 7-day token at send time
    // so each email gets a fresh, secure setup link. Showing this in the
    // editor makes it obvious the variable is recognised (vs leaving the
    // literal {{setup_url}} text which looks broken to non-technical staff).
    const setupUrlPlaceholder = `${portalBase}/portal/setup-password?token=GENERATED_AT_SEND`;
    const vars = {
      '{{first_name}}': c.first_name || '', '{{last_name}}': c.last_name || '',
      '{{name}}': `${c.first_name || ''} ${c.last_name || ''}`.trim(),
      '{{email}}': c.email || '', '{{phone}}': c.phone || '',
      '{{address}}': c.address || '', '{{city}}': c.city || '', '{{postcode}}': c.postcode || '',
      '{{system_size}}': String(c.system_size || ''), '{{system_total_cost}}': String(c.system_total_cost || ''),
      '{{deposit_amount}}': String(c.deposit_amount || ''), '{{final_bill_amount}}': String(c.final_bill_amount || ''),
      '{{provisional_install_date}}': c.provisional_install_date || '', '{{confirmed_install_date}}': c.confirmed_install_date || '',
      '{{installation_type}}': c.installation_type || '', '{{panel_manufacturer}}': c.panel_manufacturer || '',
      '{{inverter_manufacturer}}': c.inverter_manufacturer || '', '{{battery_manufacturer}}': c.battery_manufacturer || '',
      '{{panel_quantity}}': String(c.panel_quantity || ''), '{{monthly_bill}}': String(c.monthly_bill || ''),
      '{{roof_type}}': c.roof_type || '', '{{status}}': STATUS_DISPLAY_NAMES[c.status] || c.status || '',
      // Portal links — {{setup_url}} & {{reset_link}} show a placeholder
      // here so staff can see the variable IS recognised. The backend
      // swaps in a real, unique 7-day token at send time.
      '{{portal_url}}': `${portalBase}/portal/login`,
      '{{setup_url}}': setupUrlPlaceholder,
      '{{reset_link}}': setupUrlPlaceholder,
    };
    let result = text;
    for (const [k, v] of Object.entries(vars)) result = result.replaceAll(k, v);
    return result;
  };

  const handleTemplateChange = (value) => {
    setSelectedTemplate(value);
    if (value === 'none') {
      setEmailSubject('');
      setEmailBody('');
      return;
    }
    const tmpl = templates.find(t => t.trigger === value);
    if (tmpl) {
      setEmailSubject(replaceVars(tmpl.subject));
      setEmailBody(replaceVars(tmpl.body_html));
      setEditorMode('html');
    }
  };

  // Auto-apply preset template when dialog opens with `presetTrigger` (e.g.
  // after install scheduling we pop this dialog with the install-scheduled
  // template pre-selected so admin just hits Send).
  useEffect(() => {
    if (!open || !presetTrigger || templates.length === 0) return;
    handleTemplateChange(presetTrigger);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, presetTrigger, templates.length]);

  const handleAddFiles = (e) => {
    const files = Array.from(e.target.files || []);
    setAttachments(prev => [...prev, ...files]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const getFileIcon = (file) => {
    if (file.type?.startsWith('image/')) return <Image className="w-4 h-4 text-blue-500" />;
    if (file.type === 'application/pdf') return <FileText className="w-4 h-4 text-red-500" />;
    return <FileIcon className="w-4 h-4 text-slate-500" />;
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [previewState, setPreviewState] = useState({ open: false, loading: false, subject: '', body_html: '', to: '' });

  const handlePreview = async () => {
    if (!emailSubject.trim() && !emailBody.trim()) {
      toast.error('Add a subject or body before previewing');
      return;
    }
    setPreviewState(s => ({ ...s, open: true, loading: true }));
    try {
      const res = await api.post(`/customers/${customer.id}/send-email/preview`, {
        subject: restoreServerVars(emailSubject),
        body_html: restoreServerVars(getBodyHtml()),
      });
      setPreviewState({
        open: true,
        loading: false,
        subject: res.data?.subject || '',
        body_html: res.data?.body_html || '',
        to: res.data?.to || customer?.email || '',
      });
    } catch (err) {
      const msg = err.response?.data?.detail || 'Could not render preview';
      toast.error(msg);
      setPreviewState(s => ({ ...s, open: false, loading: false }));
    }
  };

  // The editor shows a friendly placeholder URL where `{{setup_url}}` /
  // `{{reset_link}}` were so staff can see the variable was recognised. When
  // sending or previewing, we swap the placeholder BACK to `{{setup_url}}`
  // so the backend can mint a fresh, secure 7-day token per email.
  const SETUP_URL_PLACEHOLDER = 'https://app.helioses.co.uk/portal/setup-password?token=GENERATED_AT_SEND';
  const restoreServerVars = (text) => {
    if (!text) return text;
    return text.split(SETUP_URL_PLACEHOLDER).join('{{setup_url}}');
  };

  const handleSend = async () => {
    if (!emailSubject.trim()) {
      toast.error('Please enter an email subject');
      return;
    }
    if (!emailBody.trim()) {
      toast.error('Please enter an email body');
      return;
    }
    if (scheduleMode === 'later') {
      if (!scheduleAt) {
        toast.error('Pick a date & time to schedule the email');
        return;
      }
      if (new Date(scheduleAt).getTime() <= Date.now()) {
        toast.error('Schedule time must be in the future');
        return;
      }
    }
    // Show confirmation before sending
    setConfirmOpen(true);
  };

  const handleConfirmedSend = async () => {
    setConfirmOpen(false);
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('subject', restoreServerVars(emailSubject));
      formData.append('body_html', restoreServerVars(getBodyHtml()));
      formData.append('cc_emails', ccEmails);
      formData.append('from_email', fromEmail);
      attachments.forEach(file => {
        formData.append('files', file);
      });

      if (scheduleMode === 'later') {
        formData.append('scheduled_at', new Date(scheduleAt).toISOString());
        await api.post(`/customers/${customer.id}/scheduled-emails`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast.success(`Email scheduled for ${new Date(scheduleAt).toLocaleString('en-GB')}`);
        setScheduledRefresh(k => k + 1);
        onOpenChange(false);
        onEmailSent?.();
      } else {
        await api.post(`/customers/${customer.id}/send-email`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast.success(`Email sent to ${customer.email}`);
        onOpenChange(false);
        onEmailSent?.();
      }
    } catch (err) {
      const msg = err.response?.data?.detail || (scheduleMode === 'later' ? 'Failed to schedule email' : 'Failed to send email');
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const name = `${customer?.first_name || ''} ${customer?.last_name || ''}`.trim();

  const textToHtml = (text) => {
    if (!text) return '';
    return text.split('\n').map(line => line.trim() ? `<p>${line}</p>` : '<br>').join('\n');
  };

  const htmlToText = (html) => {
    if (!html) return '';
    return html
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>\s*<p>/gi, '\n\n')
      .replace(/<[^>]+>/g, '')
      .replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>')
      .replace(/&nbsp;/g, ' ').replace(/&quot;/g, '"')
      .trim();
  };

  const getBodyHtml = () => {
    return editorMode === 'text' ? textToHtml(emailBody) : emailBody;
  };

  const handleEditorToggle = (mode) => {
    if (mode === editorMode) return;
    if (mode === 'text') {
      setEmailBody(htmlToText(emailBody));
    } else {
      setEmailBody(textToHtml(emailBody));
    }
    setEditorMode(mode);
  };

  const VARIABLE_GROUPS = [
    { label: 'Customer', vars: [
      { key: '{{first_name}}', label: 'First Name' },
      { key: '{{last_name}}', label: 'Last Name' },
      { key: '{{name}}', label: 'Full Name' },
      { key: '{{email}}', label: 'Email' },
      { key: '{{phone}}', label: 'Phone' },
    ]},
    { label: 'Address', vars: [
      { key: '{{address}}', label: 'Address' },
      { key: '{{city}}', label: 'City' },
      { key: '{{postcode}}', label: 'Postcode' },
    ]},
    { label: 'System', vars: [
      { key: '{{installation_type}}', label: 'Installation Type' },
      { key: '{{system_size}}', label: 'System Size' },
      { key: '{{system_total_cost}}', label: 'Total Cost' },
      { key: '{{panel_manufacturer}}', label: 'Panel Manufacturer' },
      { key: '{{panel_quantity}}', label: 'Panel Quantity' },
      { key: '{{inverter_manufacturer}}', label: 'Inverter' },
      { key: '{{battery_manufacturer}}', label: 'Battery' },
    ]},
    { label: 'Dates & Payments', vars: [
      { key: '{{provisional_install_date}}', label: 'Provisional Date' },
      { key: '{{confirmed_install_date}}', label: 'Confirmed Date' },
      { key: '{{deposit_amount}}', label: 'Deposit Amount' },
      { key: '{{final_bill_amount}}', label: 'Final Bill' },
    ]},
    { label: 'Other', vars: [
      { key: '{{status}}', label: 'Status' },
      { key: '{{monthly_bill}}', label: 'Monthly Bill' },
      { key: '{{roof_type}}', label: 'Roof Type' },
    ]},
  ];

  const insertVariable = (varKey) => {
    const textarea = bodyRef.current;
    if (!textarea) {
      setEmailBody(prev => prev + varKey);
      setShowVarPicker(false);
      return;
    }
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const before = emailBody.substring(0, start);
    const after = emailBody.substring(end);
    setEmailBody(before + varKey + after);
    setShowVarPicker(false);
    // Restore cursor position after the inserted variable
    requestAnimationFrame(() => {
      textarea.focus();
      const newPos = start + varKey.length;
      textarea.setSelectionRange(newPos, newPos);
    });
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[85vw] w-[85vw] h-[80vh] flex flex-col p-0 gap-0" data-testid="send-email-dialog">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex-shrink-0">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-amber-500" />
              Send Email to {name}
            </DialogTitle>
            <DialogDescription>
              {customer?.email
                ? `Compose an email to ${customer.email}. Select a template or write a custom message.`
                : 'Customer has no email address on file.'}
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* Existing scheduled emails for this customer */}
        {customer?.id && (
          <div className="px-6 pt-3 flex-shrink-0">
            <ScheduledEmailsBanner customerId={customer.id} refreshKey={scheduledRefresh} compact />
          </div>
        )}

        {/* Template selector + CC + From */}
        <div className="px-6 py-3 border-b border-slate-100 flex-shrink-0 space-y-3">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 flex-1">
              <label className="text-sm font-medium text-slate-600 whitespace-nowrap">Template:</label>
              <Select value={selectedTemplate} onValueChange={handleTemplateChange}>
                <SelectTrigger className="flex-1" data-testid="template-select">
                  <SelectValue placeholder="Select a template..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No template (blank email)</SelectItem>
                  {templates.filter(t => t.active !== false).map(t => (
                    <SelectItem key={t.trigger} value={t.trigger}>
                      {STATUS_DISPLAY_NAMES[t.trigger] || t.trigger}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-slate-600 whitespace-nowrap">From:</label>
              <Select value={fromEmail || 'default'} onValueChange={(v) => setFromEmail(v === 'default' ? '' : v)}>
                <SelectTrigger className="w-[200px]" data-testid="email-from-select">
                  <SelectValue placeholder="Default mailbox" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Default mailbox</SelectItem>
                  <SelectItem value="hello@helioses.co.uk">hello@helioses.co.uk</SelectItem>
                  <SelectItem value="noreply@helioses.co.uk">noreply@helioses.co.uk</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-slate-600 whitespace-nowrap">CC:</label>
            <Input
              value={ccEmails}
              onChange={(e) => setCcEmails(e.target.value)}
              placeholder="Comma-separated emails..."
              className="flex-1"
              data-testid="email-cc-input"
            />
          </div>
        </div>

        {/* Subject */}
        <div className="px-6 py-2 border-b border-slate-100 flex-shrink-0 flex items-center gap-2">
          <label className="text-sm font-medium text-slate-600 whitespace-nowrap">Subject:</label>
          <Input
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.target.value)}
            placeholder="Enter email subject..."
            className="flex-1"
            data-testid="compose-email-subject"
          />
        </div>

        {/* Split-View Editor */}
        <div className="flex-1 grid grid-cols-2 min-h-0 overflow-hidden" data-testid="compose-email-split-view">
          {/* Left: Editor */}
          <div className="flex flex-col border-r border-slate-200 min-h-0">
            <div className="px-3 py-1.5 bg-slate-50 border-b border-slate-200 flex-shrink-0 flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Edit Email</span>
              <div className="flex items-center gap-2">
                <div className="relative" ref={varBtnRef}>
                  <button
                    onClick={() => { setShowVarPicker(prev => !prev); setShowImagePicker(false); }}
                    className="flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-colors"
                    data-testid="insert-variable-btn"
                  >
                    <Braces className="w-3.5 h-3.5" />
                    Insert Variable
                    <ChevronDown className="w-3 h-3" />
                  </button>
                  {showVarPicker && (
                    <div
                      className="absolute right-0 top-full mt-1 z-50 bg-white rounded-lg shadow-lg border border-slate-200 w-64 max-h-72 overflow-y-auto"
                      data-testid="variable-picker-dropdown"
                    >
                      {VARIABLE_GROUPS.map(group => (
                        <div key={group.label}>
                          <div className="px-3 py-1.5 bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider sticky top-0 border-b border-slate-100">
                            {group.label}
                          </div>
                          {group.vars.map(v => (
                            <button
                              key={v.key}
                              onClick={() => insertVariable(v.key)}
                              className="w-full text-left px-3 py-1.5 text-sm hover:bg-amber-50 flex items-center justify-between group transition-colors"
                              data-testid={`var-${v.key.replace(/[{}]/g, '')}`}
                            >
                              <span className="text-slate-700">{v.label}</span>
                              <code className="text-xs text-slate-400 group-hover:text-amber-600 font-mono">{v.key}</code>
                            </button>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="relative" ref={imgBtnRef}>
                  <button
                    onClick={() => { setShowImagePicker(prev => !prev); setShowVarPicker(false); }}
                    className="flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200 hover:bg-purple-100 transition-colors"
                    data-testid="insert-image-btn"
                  >
                    <Image className="w-3.5 h-3.5" />
                    Insert Image
                    <ChevronDown className="w-3 h-3" />
                  </button>
                  {showImagePicker && (
                    <div
                      className="absolute right-0 top-full mt-1 z-50 bg-white rounded-lg shadow-lg border border-slate-200 w-72 max-h-80 overflow-y-auto"
                      data-testid="image-picker-dropdown"
                    >
                      {hostedImages.length === 0 ? (
                        <div className="px-4 py-6 text-center text-sm text-slate-400">
                          <Image className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          <p>No hosted images</p>
                          <p className="text-xs">Upload images in Settings &rarr; Email Images</p>
                        </div>
                      ) : (
                        hostedImages.map(img => (
                          <button
                            key={img.id}
                            onClick={() => insertImage(img)}
                            className="w-full text-left px-3 py-2 hover:bg-purple-50 flex items-center gap-3 border-b border-slate-100 last:border-b-0 transition-colors"
                            data-testid={`pick-image-${img.slug}`}
                          >
                            <div className="w-12 h-12 bg-slate-100 rounded border flex-shrink-0 flex items-center justify-center overflow-hidden">
                              <img
                                src={`${BASE_URL}/api/email-images/${img.slug}`}
                                alt={img.slug}
                                className="max-w-full max-h-full object-contain"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium text-slate-700 truncate">{img.slug}</div>
                              <div className="text-xs text-slate-400 truncate">{img.original_name}</div>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-0.5 bg-slate-200 rounded-md p-0.5" data-testid="editor-mode-toggle">
                <button
                  onClick={() => handleEditorToggle('text')}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium transition-all ${editorMode === 'text' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                  data-testid="editor-mode-text"
                >
                  <Type className="w-3.5 h-3.5" />
                  Text
                </button>
                <button
                  onClick={() => handleEditorToggle('html')}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium transition-all ${editorMode === 'html' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                  data-testid="editor-mode-html"
                >
                  <Code className="w-3.5 h-3.5" />
                  HTML
                </button>
              </div>
              </div>
            </div>
            <div className="flex-1 min-h-0 relative">
              {editorMode === 'html' ? (
                <textarea
                  ref={bodyRef}
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  className="absolute inset-0 w-full h-full resize-none font-mono text-sm p-4 bg-slate-900 text-emerald-300 border-0 focus:outline-none focus:ring-0"
                  style={{ tabSize: 2 }}
                  spellCheck={false}
                  placeholder="<p>Dear {{first_name}},</p>&#10;&#10;<p>Your email content here...</p>"
                  data-testid="compose-email-body"
                />
              ) : (
                <textarea
                  ref={bodyRef}
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  className="absolute inset-0 w-full h-full resize-none text-sm p-4 bg-white text-slate-800 border-0 focus:outline-none focus:ring-0 leading-relaxed"
                  spellCheck={true}
                  placeholder={"Dear " + (customer?.first_name || 'Customer') + ",\n\nType your message here...\n\nKind regards,\nHelios Electrical & Solar"}
                  data-testid="compose-email-body"
                />
              )}
            </div>
          </div>
          {/* Right: Live Preview */}
          <div className="flex flex-col min-h-0">
            <div className="px-3 py-1.5 bg-slate-50 border-b border-slate-200 flex-shrink-0">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Preview</span>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto bg-slate-100 p-4">
              <div className="max-w-xl mx-auto bg-white rounded-lg shadow-sm border overflow-hidden">
                <div className="bg-slate-50 px-4 py-2 border-b space-y-1">
                  <div className="flex items-center gap-2 text-xs">
                    <span className="font-medium text-slate-500 w-12">To:</span>
                    <span>{name} &lt;{customer?.email}&gt;</span>
                  </div>
                  {ccEmails && (
                    <div className="flex items-center gap-2 text-xs">
                      <span className="font-medium text-slate-500 w-12">CC:</span>
                      <span>{ccEmails}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-xs">
                    <span className="font-medium text-slate-500 w-12">Subject:</span>
                    <span className="font-medium text-slate-900">{emailSubject || '(No subject)'}</span>
                  </div>
                </div>
                <div className="p-5" data-testid="compose-email-preview-body">
                  {emailBody ? (
                    <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: getBodyHtml() }} />
                  ) : (
                    <p className="text-sm text-slate-400 italic">Start typing to see preview...</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Attachments bar */}
        <div className="px-6 py-2 border-t border-slate-200 flex-shrink-0">
          <div className="flex items-center gap-3">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleAddFiles}
              multiple
              className="hidden"
              data-testid="email-attach-input"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              data-testid="attach-files-btn"
            >
              <Paperclip className="w-4 h-4 mr-1" />
              Attach Files
            </Button>
            {attachments.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap flex-1 overflow-x-auto">
                {attachments.map((file, idx) => (
                  <Badge
                    key={idx}
                    className="bg-slate-100 text-slate-700 border border-slate-200 px-2 py-1 flex items-center gap-1.5 cursor-default"
                    data-testid={`attachment-${idx}`}
                  >
                    {getFileIcon(file)}
                    <span className="text-xs max-w-[120px] truncate">{file.name}</span>
                    <span className="text-xs text-slate-400">({formatSize(file.size)})</span>
                    <button
                      onClick={() => handleRemoveAttachment(idx)}
                      className="ml-0.5 hover:text-red-500 transition-colors"
                      data-testid={`remove-attachment-${idx}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-between flex-shrink-0 bg-white gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cancel-send-email">
            Cancel
          </Button>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {/* Send now / Schedule toggle */}
            <div className="flex items-center rounded-md border border-slate-200 overflow-hidden" data-testid="send-mode-toggle">
              <button type="button"
                className={`px-3 py-1.5 text-sm ${scheduleMode === 'now' ? 'bg-blue-600 text-white' : 'bg-white text-slate-600'}`}
                onClick={() => setScheduleMode('now')} data-testid="send-mode-now">
                Send now
              </button>
              <button type="button"
                className={`px-3 py-1.5 text-sm border-l border-slate-200 ${scheduleMode === 'later' ? 'bg-blue-600 text-white' : 'bg-white text-slate-600'}`}
                onClick={() => setScheduleMode('later')} data-testid="send-mode-later">
                Schedule
              </button>
            </div>
            {scheduleMode === 'later' && (
              <Input type="datetime-local" value={scheduleAt} min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
                onChange={(e) => setScheduleAt(e.target.value)} className="w-[210px] h-9" data-testid="schedule-at-input" />
            )}
            <Button
              variant="outline"
              onClick={handlePreview}
              disabled={!customer?.id || (!emailSubject.trim() && !emailBody.trim())}
              data-testid="preview-email-btn"
              title="See exactly what the customer will receive — all variables resolved"
            >
              <Eye className="w-4 h-4 mr-2" />
              Preview
            </Button>
            <Button
              onClick={handleSend}
              disabled={sending || !customer?.email || !emailSubject.trim() || !emailBody.trim() || (scheduleMode === 'later' && !scheduleAt)}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="send-email-btn"
            >
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : (scheduleMode === 'later' ? <Calendar className="w-4 h-4 mr-2" /> : <Send className="w-4 h-4 mr-2" />)}
              {scheduleMode === 'later' ? 'Schedule Email' : 'Send Email'}{attachments.length > 0 ? ` (${attachments.length})` : ''}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Email Send Confirmation */}
    <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
      <DialogContent className="max-w-md" aria-describedby="confirm-email-desc">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-600">
            <Send className="w-5 h-5" />
            Confirm Send Email
          </DialogTitle>
        </DialogHeader>
        <p id="confirm-email-desc" className="text-sm text-slate-600 dark:text-slate-400">
          You are about to send an email to:
        </p>
        <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3 space-y-1.5 text-sm">
          <div><span className="font-medium text-slate-500">To:</span> {customer?.email}</div>
          {ccEmails && <div><span className="font-medium text-slate-500">CC:</span> {ccEmails}</div>}
          <div><span className="font-medium text-slate-500">Subject:</span> {emailSubject}</div>
          {attachments.length > 0 && <div><span className="font-medium text-slate-500">Attachments:</span> {attachments.length} file(s)</div>}
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="cancel-confirm-email">
            Go Back
          </Button>
          <Button onClick={handleConfirmedSend} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="confirm-send-email-btn">
            <Send className="w-4 h-4 mr-2" />
            Confirm & Send
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    {/* Email Preview — shows the rendered email exactly as the customer will see it */}
    <Dialog open={previewState.open} onOpenChange={(o) => setPreviewState(s => ({ ...s, open: o }))}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col" aria-describedby="preview-email-desc" data-testid="email-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-blue-600">
            <Eye className="w-5 h-5" />
            Email Preview — what {customer?.first_name || 'the customer'} will see
          </DialogTitle>
        </DialogHeader>
        <p id="preview-email-desc" className="sr-only">
          Rendered preview of the email with all template variables resolved.
        </p>

        {previewState.loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto space-y-3">
            <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3 space-y-1.5 text-sm border border-slate-200 dark:border-slate-700">
              <div><span className="font-medium text-slate-500">To:</span> {previewState.to}</div>
              {ccEmails && <div><span className="font-medium text-slate-500">CC:</span> {ccEmails}</div>}
              {fromEmail && <div><span className="font-medium text-slate-500">From:</span> {fromEmail}</div>}
              <div><span className="font-medium text-slate-500">Subject:</span> <span className="font-semibold text-slate-900 dark:text-slate-100" data-testid="preview-subject">{previewState.subject || <em className="text-slate-400 font-normal">(no subject)</em>}</span></div>
            </div>

            <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
              <div className="bg-slate-100 dark:bg-slate-800 px-3 py-2 text-xs font-medium text-slate-600 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                Email body
              </div>
              <iframe
                title="Email body preview"
                srcDoc={previewState.body_html || '<p style="color:#94a3b8;font-family:sans-serif;padding:16px">(empty body)</p>'}
                sandbox=""
                className="w-full bg-white"
                style={{ height: '50vh', border: 0 }}
                data-testid="preview-iframe"
              />
            </div>

            {(emailBody.includes('{{setup_url}}') || emailBody.includes('{{reset_link}}')) && (
              <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3 text-xs text-amber-800 dark:text-amber-300">
                <strong>Note:</strong> The portal-setup link shows a placeholder here. A unique, secure token will be minted at the moment you click <em>Send</em>, so each email gets a fresh link valid for 7 days.
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-3 border-t border-slate-200 dark:border-slate-700">
          <Button variant="outline" onClick={() => setPreviewState(s => ({ ...s, open: false }))} data-testid="close-preview-btn">
            Close
          </Button>
          <Button
            onClick={() => { setPreviewState(s => ({ ...s, open: false })); handleSend(); }}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            disabled={sending || !customer?.email || !emailSubject.trim() || !emailBody.trim()}
            data-testid="preview-send-btn"
          >
            <Send className="w-4 h-4 mr-2" />
            Looks good — Send
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};
